import {
  NARRATOR_LINES, CALLBACK_CHAINS, AMBIENT_KEYS,
  type NarratorKey, type NarratorTier, type KeyDef,
} from './narrator-lines'
import { showCaption, hideCaption } from '../ui/narrator-caption'
import { whenAudioReady, playVoiceBuffer } from './audio'
import { T } from '../game/tunables'

const LS_KEY = 'amoeba.narrator.firedOnce'

const TIER_PRIORITY: Record<NarratorTier, number> = {
  milestone: 0,
  event: 1,
  ambient: 2,
}

const audioBuffers = new Map<string, AudioBuffer>()
let audioDecodeStarted = false

function bufferKey(key: NarratorKey, idx: number): string {
  return `${key}_${idx}`
}

export async function loadNarratorAudio(): Promise<void> {
  if (audioDecodeStarted) return
  audioDecodeStarted = true
  const ctx = await whenAudioReady()
  const tasks: Promise<void>[] = []
  for (const [key, def] of Object.entries(NARRATOR_LINES) as [NarratorKey, KeyDef][]) {
    for (let i = 0; i < def.lines.length; i++) {
      const id = bufferKey(key, i)
      tasks.push(
        fetch(`/audio/narrator/${id}.mp3`)
          .then(r => {
            if (!r.ok) throw new Error(`HTTP ${r.status}`)
            // Vite's dev server SPA-falls-back to index.html for missing
            // assets; without this guard, decodeAudioData chokes on HTML
            // with a confusing EncodingError. Check the content-type so a
            // missing file reports honestly as missing.
            const ct = r.headers.get('content-type') ?? ''
            if (!ct.startsWith('audio/')) {
              throw new Error(`missing audio file (got content-type: ${ct || 'unknown'}) — run "npm run gen-narrator"`)
            }
            return r.arrayBuffer()
          })
          .then(buf => ctx.decodeAudioData(buf))
          .then(audio => { audioBuffers.set(id, audio) })
          .catch(err => console.warn(`[narrator] failed to load ${id}:`, err)),
      )
    }
  }
  await Promise.all(tasks)
}

interface QueueEntry {
  key: NarratorKey
  tier: NarratorTier
  variantIndex: number
}

type Phase = 'idle' | 'playing' | 'gap'

const st = {
  phase: 'idle' as Phase,
  phaseEnd: 0,                         // performance.now() when phase ends
  queue: [] as QueueEntry[],
  cooldowns: new Map<NarratorKey, number>(),
  firedOnce: loadFiredOnce(),
  shuffleBags: new Map<NarratorKey, number[]>(),
  lastPlayedIndex: new Map<NarratorKey, number>(),
  chainTimestamps: new Map<NarratorKey, number>(),
  idle: false,
  idleSec: 0,
  lastAmbientMs: -(T.narrator.ambientCooldownMs + 1),
  ambientBag: [] as NarratorKey[],
  lastAmbientKey: null as NarratorKey | null,
  bathroomSilenceEnd: 0,
  bathroomUsed: false,
  shopkeeperSpeaking: false,
}

function loadFiredOnce(): Set<NarratorKey> {
  try {
    const raw = localStorage.getItem(LS_KEY)
    if (raw) return new Set(JSON.parse(raw) as NarratorKey[])
  } catch { /* ignore */ }
  return new Set()
}

function saveFiredOnce(): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify([...st.firedOnce]))
  } catch { /* ignore */ }
}

function inBathroomSilence(): boolean {
  return st.bathroomSilenceEnd > 0 && performance.now() < st.bathroomSilenceEnd
}

function isCallbackChainExempt(key: NarratorKey): boolean {
  const now = performance.now()
  for (const chain of CALLBACK_CHAINS) {
    if (chain.second !== key) continue
    const t = st.chainTimestamps.get(chain.first)
    if (t !== undefined && now - t < chain.windowSec * 1000) return true
  }
  return false
}

function pickVariant(key: NarratorKey): number {
  const count = (NARRATOR_LINES[key] as KeyDef).lines.length
  if (count === 1) return 0

  let bag = st.shuffleBags.get(key)
  if (!bag || bag.length === 0) {
    bag = Array.from({ length: count }, (_, i) => i)
    for (let i = bag.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [bag[i], bag[j]] = [bag[j], bag[i]]
    }
    // Prevent back-to-back repeat across reshuffle seam
    const last = st.lastPlayedIndex.get(key)
    if (last !== undefined && bag[0] === last && bag.length > 1) {
      [bag[0], bag[1]] = [bag[1], bag[0]]
    }
    st.shuffleBags.set(key, bag)
  }

  const idx = bag.shift()!
  st.shuffleBags.set(key, bag)
  return idx
}

function pickAmbientKey(): NarratorKey | null {
  if (st.ambientBag.length === 0) {
    st.ambientBag = [...AMBIENT_KEYS]
    for (let i = st.ambientBag.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [st.ambientBag[i], st.ambientBag[j]] = [st.ambientBag[j], st.ambientBag[i]]
    }
    // Prevent back-to-back across seam
    if (st.lastAmbientKey && st.ambientBag[0] === st.lastAmbientKey && st.ambientBag.length > 1) {
      [st.ambientBag[0], st.ambientBag[1]] = [st.ambientBag[1], st.ambientBag[0]]
    }
  }
  return st.ambientBag.shift() ?? null
}

function startPlaying(entry: QueueEntry): void {
  const line = (NARRATOR_LINES[entry.key] as KeyDef).lines[entry.variantIndex]
  st.phase = 'playing'
  st.lastPlayedIndex.set(entry.key, entry.variantIndex)

  const buf = audioBuffers.get(bufferKey(entry.key, entry.variantIndex))
  if (buf) {
    playVoiceBuffer(buf)
    st.phaseEnd = performance.now() + buf.duration * 1000
  } else {
    // Caption-only fallback (audio not yet decoded, or fetch failed)
    if (audioDecodeStarted) {
      console.debug(`[narrator] no buffer for ${bufferKey(entry.key, entry.variantIndex)}, caption-only`)
    }
    st.phaseEnd = performance.now() + T.narrator.captionDurationSec * 1000
  }

  showCaption(line.text)
}

function enqueue(entry: QueueEntry): void {
  if (st.phase === 'idle') {
    startPlaying(entry)
    return
  }

  if (st.queue.length < 2) {
    const insertAt = st.queue.findIndex(q => TIER_PRIORITY[q.tier] > TIER_PRIORITY[entry.tier])
    if (insertAt === -1) st.queue.push(entry)
    else st.queue.splice(insertAt, 0, entry)
    return
  }

  // Queue full — drop lowest priority between last queued and new entry
  const last = st.queue[st.queue.length - 1]
  if (TIER_PRIORITY[last.tier] > TIER_PRIORITY[entry.tier]) {
    console.debug(`[narrator] queue overflow: dropped ${last.key}`)
    st.queue.pop()
    const insertAt = st.queue.findIndex(q => TIER_PRIORITY[q.tier] > TIER_PRIORITY[entry.tier])
    if (insertAt === -1) st.queue.push(entry)
    else st.queue.splice(insertAt, 0, entry)
  } else {
    console.debug(`[narrator] queue overflow: dropped ${entry.key}`)
  }
}

export function triggerNarrator(key: NarratorKey): void {
  const def = NARRATOR_LINES[key] as KeyDef
  const tier: NarratorTier = def.tier
  const now = performance.now()

  if (def.oneShot && st.firedOnce.has(key)) return
  if (st.shopkeeperSpeaking && tier !== 'milestone') return
  if (inBathroomSilence() && tier !== 'milestone') return

  if (tier === 'event' && !def.oneShot) {
    const last = st.cooldowns.get(key)
    if (last !== undefined && now - last < T.narrator.eventCooldownMs) {
      if (!isCallbackChainExempt(key)) return
    }
  }

  st.cooldowns.set(key, now)
  st.chainTimestamps.set(key, now)

  if (def.oneShot) {
    st.firedOnce.add(key)
    saveFiredOnce()
  }

  enqueue({ key, tier, variantIndex: pickVariant(key) })
}

export function setNarratorIdle(isIdle: boolean): void {
  if (!isIdle) st.idleSec = 0
  st.idle = isIdle
}

export function setShopkeeperSpeaking(active: boolean): void {
  st.shopkeeperSpeaking = active
}

export function clearNarratorOnRespawn(): void {
  st.queue = []
  if (st.phase !== 'idle') {
    st.phase = 'idle'
    hideCaption(0.3)
  }
}

export function tickNarrator(dt: number): void {
  const now = performance.now()

  // Advance phase state machine
  if (st.phase === 'playing' && now >= st.phaseEnd) {
    if (st.queue.length > 0) {
      st.phase = 'gap'
      st.phaseEnd = now + T.narrator.queueGapMs
      hideCaption(0.15)
    } else {
      st.phase = 'idle'
      hideCaption(0.5)
    }
  }

  if (st.phase === 'gap' && now >= st.phaseEnd) {
    const next = st.queue.shift()
    if (!next || (next.tier === 'ambient' && !st.idle)) {
      st.phase = 'idle'
    } else {
      startPlaying(next)
    }
  }

  // Ambient idle accumulation
  if (st.idle) {
    st.idleSec += dt
  }

  // Ambient trigger
  if (
    st.phase === 'idle' &&
    st.queue.length === 0 &&
    st.idle &&
    st.idleSec >= T.narrator.ambientIdleMinSec &&
    !st.shopkeeperSpeaking &&
    !inBathroomSilence() &&
    now - st.lastAmbientMs >= T.narrator.ambientCooldownMs
  ) {
    const key = pickAmbientKey()
    if (key) {
      st.lastAmbientKey = key
      st.lastAmbientMs = now

      if (key === 'idle_bathroom' && !st.bathroomUsed) {
        st.bathroomUsed = true
        const silenceMs = T.narrator.bathroomSilenceMinMs +
          Math.random() * (T.narrator.bathroomSilenceMaxMs - T.narrator.bathroomSilenceMinMs)
        st.bathroomSilenceEnd = now + T.narrator.captionDurationSec * 1000 + silenceMs
      }

      st.idleSec = 0
      enqueue({ key, tier: 'ambient', variantIndex: 0 })
    }
  }
}
